import React, { useState } from "react"

import { cn } from "@pma/utils/helpers"
import { PlusIcon, ChevronDownIcon, CalendarIcon } from "@radix-ui/react-icons"
import { useFormContext, Controller } from "react-hook-form"
import { Calendar } from "@components/common/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { format, parse } from "date-fns"
import { he } from "date-fns/locale"
import useTranslator from "@pma/hooks/use-translator"

// ==================== FormField ====================
export const FormField: React.FC<{
  label: string
  required?: boolean
  children: React.ReactNode
  className?: string
  error?: string
}> = ({ label, required, children, className, error }) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <label className='text-sm font-medium text-slate-600'>
      {label}
      {required && <span className='ms-0.5 text-red-500'>*</span>}
    </label>
    {children}
    {error && <span className='text-xs text-red-500'>{error}</span>}
  </div>
)

// ==================== RHFInput (connected to react-hook-form) ====================
export const RHFInput: React.FC<{
  name: string
  placeholder: string
  type?: string
  disabled?: boolean
  className?: string
}> = ({ name, placeholder, type = "text", disabled, className }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext()

  // Resolve nested errors (e.g. "workDetails.0.description")
  const error = name.split(".").reduce((obj: any, key) => obj?.[key], errors)

  return (
    <div className='flex flex-col gap-1'>
      <input
        type={type}
        {...register(name)}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-700 transition-all duration-200",
          "placeholder:text-slate-400",
          "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
          "disabled:cursor-not-allowed disabled:bg-slate-50",
          error ? "border-red-400 focus:border-red-500 focus:ring-red-500/20" : "border-slate-200",
          className,
        )}
      />
      {error?.message && (
        <span className='text-xs text-red-500'>{error.message as string}</span>
      )}
    </div>
  )
}

// ==================== Input (uncontrolled – for manual array field use) ====================
export const Input: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder: string
  type?: string
  disabled?: boolean
  className?: string
  error?: string
}> = ({ value, onChange, placeholder, type = "text", disabled, className, error }) => (
  <div className='flex flex-col gap-1'>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        "rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-700 transition-all duration-200",
        "placeholder:text-slate-400",
        "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
        "disabled:cursor-not-allowed disabled:bg-slate-50",
        error ? "border-red-400 focus:border-red-500 focus:ring-red-500/20" : "border-slate-200",
        className,
      )}
    />
    {error && <span className='text-xs text-red-500'>{error}</span>}
  </div>
)

// ==================== RHFTextArea (connected to react-hook-form) ====================
export const RHFTextArea: React.FC<{
  name: string
  placeholder: string
  rows?: number
  className?: string
}> = ({ name, placeholder, rows = 3, className }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext()

  const error = name.split(".").reduce((obj: any, key) => obj?.[key], errors)

  return (
    <div className='flex flex-col w-full gap-1'>
      <textarea
        {...register(name)}
        placeholder={placeholder}
        rows={rows}
        className={cn(
          "min-h-[80px] resize-y rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-700 transition-all duration-200",
          "placeholder:text-slate-400",
          "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
          error ? "border-red-400 focus:border-red-500 focus:ring-red-500/20" : "border-slate-200",
          className,
        )}
      />
      {error?.message && (
        <span className='text-xs text-red-500'>{error.message as string}</span>
      )}
    </div>
  )
}

// ==================== TextArea (uncontrolled) ====================
export const TextArea: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder: string
  rows?: number
  className?: string
}> = ({ value, onChange, placeholder, rows = 3, className }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className={cn(
      "min-h-[80px] resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 transition-all duration-200",
      "placeholder:text-slate-400",
      "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
      className,
    )}
  />
)

// ==================== RHFSelect (connected to react-hook-form) ====================
export const RHFSelect: React.FC<{
  name: string
  options: { value: string; label: string }[]
  placeholder: string
  hasAddButton?: boolean
  onAdd?: () => void
  className?: string
}> = ({ name, options, placeholder, hasAddButton, onAdd, className }) => {
  const {
    formState: { errors },
  } = useFormContext()

  const error = name.split(".").reduce((obj: any, key) => obj?.[key], errors)

  return (
    <div className='flex flex-col gap-1'>
      <div className='relative flex gap-2'>
        <div className='relative flex-1'>
          <Controller
            name={name}
            render={({ field }) => (
              <select
                {...field}
                value={field.value as string}
                className={cn(
                  "w-full cursor-pointer appearance-none rounded-lg border bg-white px-3 py-2.5 pe-8 text-sm text-slate-700 transition-all duration-200",
                  "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
                  !field.value && "text-slate-400",
                  error
                    ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                    : "border-slate-200",
                  className,
                )}
              >
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          />
          <ChevronDownIcon className='pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
        </div>
        {hasAddButton && (
          <button
            type='button'
            onClick={onAdd}
            className='flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600 text-white transition-colors duration-200 hover:bg-indigo-700'
          >
            <PlusIcon className='h-4 w-4' />
          </button>
        )}
      </div>
      {error?.message && (
        <span className='text-xs text-red-500'>{error.message as string}</span>
      )}
    </div>
  )
}

// ==================== Select (uncontrolled) ====================
export const Select: React.FC<{
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder: string
  hasAddButton?: boolean
  onAdd?: () => void
  className?: string
  error?: string
}> = ({ value, onChange, options, placeholder, hasAddButton, onAdd, className, error }) => (
  <div className='flex flex-col gap-1'>
    <div className='relative flex gap-2'>
      <div className='relative flex-1'>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "w-full cursor-pointer appearance-none rounded-lg border bg-white px-3 py-2.5 pe-8 text-sm text-slate-700 transition-all duration-200",
            "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
            !value && "text-slate-400",
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
              : "border-slate-200",
            className,
          )}
        >
          <option value=''>{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon className='pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
      </div>
      {hasAddButton && (
        <button
          type='button'
          onClick={onAdd}
          className='flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600 text-white transition-colors duration-200 hover:bg-indigo-700'
        >
          <PlusIcon className='h-4 w-4' />
        </button>
      )}
    </div>
    {error && <span className='text-xs text-red-500'>{error}</span>}
  </div>
)

// ==================== RHFDatePicker (Calendar-based, connected to react-hook-form) ====================
export const RHFDatePicker: React.FC<{
  name: string
  placeholder?: string
  className?: string
}> = ({ name, placeholder = "Select a date", className }) => {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"
  const [open, setOpen] = useState(false)

  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error } }) => {
        const dateValue = field.value ? parse(field.value, "yyyy-MM-dd", new Date()) : undefined
        const displayValue = dateValue
          ? format(dateValue, "PPP", { locale: isHebrew ? he : undefined })
          : null

        return (
          <div className='flex flex-col gap-1'>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-sm transition-all duration-200",
                    "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
                    displayValue ? "text-slate-700" : "text-slate-400",
                    error
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                      : "border-slate-200",
                    className,
                  )}
                >
                  <span>{displayValue || placeholder}</span>
                  <CalendarIcon className='h-4 w-4 text-slate-400' />
                </button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0' align='start'>
                <Calendar
                  mode='single'
                  locale={isHebrew ? he : undefined}
                  selected={dateValue}
                  onSelect={(date) => {
                    if (date) {
                      field.onChange(format(date, "yyyy-MM-dd"))
                    }
                    setOpen(false)
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {error?.message && (
              <span className='text-xs text-red-500'>{error.message as string}</span>
            )}
          </div>
        )
      }}
    />
  )
}

// ==================== DatePicker (uncontrolled) ====================
export const DatePicker: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  error?: string
}> = ({ value, onChange, placeholder = "Select a date", className, error }) => {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"
  const [open, setOpen] = useState(false)

  const dateValue = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined
  const displayValue = dateValue
    ? format(dateValue, "PPP", { locale: isHebrew ? he : undefined })
    : null

  return (
    <div className='flex flex-col gap-1'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            className={cn(
              "flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-sm transition-all duration-200",
              "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
              displayValue ? "text-slate-700" : "text-slate-400",
              error
                ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                : "border-slate-200",
              className,
            )}
          >
            <span>{displayValue || placeholder}</span>
            <CalendarIcon className='h-4 w-4 text-slate-400' />
          </button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='single'
            locale={isHebrew ? he : undefined}
            selected={dateValue}
            onSelect={(date) => {
              if (date) {
                onChange(format(date, "yyyy-MM-dd"))
              }
              setOpen(false)
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {error && <span className='text-xs text-red-500'>{error}</span>}
    </div>
  )
}

// ==================== AutoCompleteInput (connected to react-hook-form) ====================
interface AutoCompleteOption {
  id: string | number
  value: string
}

export const AutoCompleteInput: React.FC<{
  name: string
  options: AutoCompleteOption[]
  value: string | number
  onChange?: (value: string | number) => void
  freesolo?: boolean
  useForm?: boolean
  placeholder?: string
  required?: boolean
  showAllOptionsOnSelect?: boolean
}> = ({ name, options, value, onChange, freesolo = false, useForm: useFormProp = true, placeholder, required, showAllOptionsOnSelect = false }) => {
  const { translate } = useTranslator()

  const context = useFormProp ? useFormContext() : null
  const control = context?.control
  const [inputValue, setInputValue] = useState("")
  const [showOptions, setShowOptions] = useState(false)
  const [isTyping, setIsTyping] = useState(false)

  // Sync initial value (id or text) to input text
  React.useEffect(() => {
    const match = options?.find((opt) => opt.id === value)
    if (match) {
      setInputValue(match.value)
      setIsTyping(false)
    } else if (typeof value === "string") {
      setInputValue(value)
      if (value === "") setIsTyping(false)
    }
  }, [value, options])

  const filteredOptions =
    showAllOptionsOnSelect && !isTyping
      ? options
      : options?.filter((opt) =>
          opt.value.toLowerCase().includes(inputValue.toLowerCase()),
        )

  const handleInputChange = (inputVal: string, rhfOnChange?: (val: any) => void) => {
    setInputValue(inputVal)
    setShowOptions(true)
    setIsTyping(true)

    if (freesolo) {
      rhfOnChange?.(inputVal)
      onChange?.(inputVal)
    } else {
      if (inputVal === "") {
        rhfOnChange?.("")
        onChange?.("")
      }
    }
  }

  const handleOptionSelect = (selected: AutoCompleteOption, rhfOnChange?: (val: any) => void) => {
    rhfOnChange?.(selected.id)
    onChange?.(selected.id)
    setInputValue(selected.value)
    setShowOptions(false)
    setIsTyping(false)
  }

  const computedPlaceholder = required
    ? `${translate(placeholder ?? "Select or type a resource")} *`
    : translate(placeholder ?? "Select or type a resource")

  const renderInput = (field?: any, fieldState?: any) => (
    <div className='relative'>
      <input
        type='text'
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value, field?.onChange)}
        onFocus={() => setShowOptions(true)}
        onBlur={() => setTimeout(() => setShowOptions(false), 100)}
        className={cn(
          "w-full rounded-lg border bg-white px-3 py-2.5 pe-8 text-sm text-slate-700 transition-all duration-200",
          "placeholder:text-slate-400",
          "focus:ring-indigo-500/20 focus:border-indigo-500 focus:outline-none focus:ring-2",
          fieldState?.error
            ? "border-red-400 focus:border-red-500 focus:ring-red-500/20"
            : "border-slate-200",
        )}
        placeholder={computedPlaceholder}
      />

      {/* Clear Button */}
      {inputValue && (
        <button
          type='button'
          className='absolute end-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500'
          onClick={() => {
            setInputValue("")
            setShowOptions(false)
            setIsTyping(false)
            field?.onChange?.("")
            onChange?.("")
          }}
        >
          ×
        </button>
      )}

      {showOptions && filteredOptions?.length > 0 && (
        <ul className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg'>
          {filteredOptions.map((opt) => (
            <li
              key={opt.id}
              onMouseDown={() => handleOptionSelect(opt, field?.onChange)}
              className='cursor-pointer px-3 py-2 text-sm hover:bg-indigo-50'
            >
              {opt.value}
            </li>
          ))}
        </ul>
      )}

      {fieldState?.error && (
        <span className='text-xs text-red-500'>{fieldState.error.message}</span>
      )}
    </div>
  )

  if (control) {
    return (
      <Controller
        control={control}
        name={name}
        defaultValue={value}
        render={({ field, fieldState }) => renderInput(field, fieldState)}
      />
    )
  }

  return renderInput()
}

// ==================== SectionCard ====================
export const SectionCard: React.FC<{
  title: string
  onAdd?: () => void
  addLabel?: string
  children: React.ReactNode
  className?: string
}> = ({ title, onAdd, addLabel, children, className }) => (
  <div className={cn("rounded-xl border border-slate-200 bg-white", className)}>
    <div className='flex items-center justify-between border-b border-slate-100 px-3 py-2.5 sm:px-4 sm:py-3'>
      <h4 className='text-xs font-semibold text-slate-800 sm:text-sm'>{title}</h4>
      {onAdd && (
        <button
          type='button'
          onClick={onAdd}
          className='flex flex-shrink-0 items-center gap-1.5 rounded-full border border-[#6938EF] bg-white px-3 py-1.5 text-xs font-semibold text-[#6938EF] transition-colors hover:bg-[#6938EF]/5 sm:px-4 sm:py-2 sm:text-sm'
        >
          <PlusIcon className='h-3.5 w-3.5 sm:h-4 sm:w-4' />
          {addLabel || "Add Item"}
        </button>
      )}
    </div>
    <div className='p-3 sm:p-4'>{children}</div>
  </div>
)